import AddIcon from '@mui/icons-material/Add'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import { Button, Checkbox, FormControlLabel, MenuItem, Select, TextField } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import React from 'react'
import { Link } from 'react-router-dom'
import { FilterSelect, filterSx, LookupField } from '../../../components/filter-fields'
import GrantContent from '../../../components/GrantContent'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import { searchClientes, searchVendedores } from '../../../utils/lookups'
import * as Permisos from '../../../utils/Permisos'
import { formatCount, money, RamoMark } from '../common/policy-common'
import PolicyDrawer from '../drawer/policy-drawer'
import {
    NEW_POLICY_LOCATION,
    PAGE_SIZES,
    RAMOS,
    usePolicyListModel,
    YEARS,
} from './policy-list-model'
import type { PolicyTextFilter } from './policy-list-model'

const SKELETON_ROWS = [1, 2, 3, 4, 5, 6, 7, 8]

const columns = [
    { label: 'Póliza', className: 'min-w-0 md:min-w-[180px]' },
    { label: 'Cliente', className: 'hidden min-w-[180px] md:table-cell' },
    { label: 'Concepto', className: 'hidden min-w-[160px] lg:table-cell' },
    { label: 'Aseguradora', className: 'hidden whitespace-nowrap lg:table-cell' },
    { label: 'Estado', className: 'hidden whitespace-nowrap md:table-cell' },
    { label: 'Vigencia', className: 'hidden whitespace-nowrap lg:table-cell' },
    { label: 'Total', className: 'hidden whitespace-nowrap text-right md:table-cell' },
    { label: 'Acciones', className: 'hidden whitespace-nowrap text-right md:table-cell', sr: true },
]

const TEXT_FILTERS: Array<{ key: PolicyTextFilter; label: string; width: number }> = [
    { key: 'noPoliza', label: 'Número de póliza', width: 180 },
    { key: 'concepto', label: 'Concepto', width: 180 },
    { key: 'abreviacion', label: 'Aseguradora', width: 160 },
]

const editBtnSx: SxProps<Theme> = {
    width: 'auto',
    minWidth: 'auto',
    height: 36,
    padding: '0 12px',
    borderRadius: '10px',
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '13px',
    lineHeight: 1,
    backgroundColor: 'transparent',
    color: 'rgb(var(--k-ink))',
    border: '1px solid rgb(var(--k-line-strong))',
    '&:hover': {
        backgroundColor: 'rgb(var(--k-accent-soft))',
        border: '1px solid rgb(var(--k-primary))',
        color: 'rgb(var(--k-accent-ink))',
        boxShadow: 'none',
    },
}

const stopRowClick = (event: React.MouseEvent) => event.stopPropagation()

const PolicyList = () => {
    const model = usePolicyListModel()
    const { filters, loading } = model

    const tableHead = (
        <thead>
            <tr className="text-left text-xs font-bold uppercase tracking-[0.05em] text-k-muted">
                {columns.map((column) => (
                    <th
                        key={column.label}
                        className={`sticky top-0 z-10 bg-k-surface px-2.5 py-3 font-bold ${column.className} ${column.label === 'Póliza' ? 'pl-4' : ''} ${column.sr ? 'pr-4 text-right' : ''} ${column.label === 'Total' ? 'pr-2.5' : ''}`}
                    >
                        {column.sr ? <span className="sr-only">{column.label}</span> : column.label}
                    </th>
                ))}
            </tr>
        </thead>
    )

    return (
        <KarteraAdminTemplate loading={false} padded={false} title="Pólizas">
            <PolicyDrawer polizaId={model.detailId} onClose={model.closePoliza} />

            <div className="flex min-h-0 flex-1 flex-col gap-[18px] bg-k-bg px-[clamp(16px,2.5vw,32px)] pb-10 pt-6">
                {loading && !model.initialLoadDone && (
                    <div className="flex min-h-0 flex-1 flex-col gap-[18px]">
                        <div>
                            <div className="mb-2 h-7 w-32 animate-pulse rounded bg-k-line md:h-8 md:w-40" />
                            <div className="hidden h-4 w-64 animate-pulse rounded bg-k-line md:block" />
                        </div>
                        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[18px] border border-k-line bg-k-surface">
                            <div className="shrink-0 border-b border-k-line px-4 py-3.5">
                                <div className="h-[42px] max-w-[380px] animate-pulse rounded-xl bg-k-line" />
                            </div>
                            <div className="min-h-0 flex-1 overflow-auto">
                                <table className="w-full table-fixed border-collapse md:min-w-[980px] md:table-auto">
                                    {tableHead}
                                    <tbody className="bg-k-surface">
                                        {SKELETON_ROWS.map((row) => (
                                            <tr key={row} className="border-t border-k-line">
                                                <td className="px-4 py-2.5">
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-9 w-9 animate-pulse rounded-[11px] bg-k-line" />
                                                        <div>
                                                            <div className="h-3.5 w-28 animate-pulse rounded bg-k-line" />
                                                            <div className="mt-1 h-3 w-16 animate-pulse rounded bg-k-line" />
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="hidden px-2.5 py-2.5 md:table-cell"><div className="h-3.5 w-36 animate-pulse rounded bg-k-line" /></td>
                                                <td className="hidden px-2.5 py-2.5 lg:table-cell"><div className="h-3.5 w-32 animate-pulse rounded bg-k-line" /></td>
                                                <td className="hidden px-2.5 py-2.5 lg:table-cell"><div className="h-3.5 w-16 animate-pulse rounded bg-k-line" /></td>
                                                <td className="hidden px-2.5 py-2.5 md:table-cell"><div className="h-5 w-16 animate-pulse rounded-full bg-k-line" /></td>
                                                <td className="hidden px-2.5 py-2.5 lg:table-cell"><div className="h-3.5 w-24 animate-pulse rounded bg-k-line" /></td>
                                                <td className="hidden px-2.5 py-2.5 text-right md:table-cell"><div className="ml-auto h-3.5 w-20 animate-pulse rounded bg-k-line" /></td>
                                                <td className="hidden px-4 py-2.5 text-right md:table-cell"><div className="ml-auto h-9 w-36 animate-pulse rounded-[10px] bg-k-line" /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {model.error && !loading && (
                    <div className="mb-6 rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                        <div className="flex items-center">
                            <ErrorOutlineIcon className="mr-2 text-k-danger" style={{ fontSize: 20 }} />
                            <p className="text-sm text-k-danger-ink">{model.error}</p>
                        </div>
                        <Button
                            variant="contained"
                            onClick={model.reload}
                            className="mt-3 !bg-k-danger !text-white"
                        >
                            Reintentar
                        </Button>
                    </div>
                )}

                {model.initialLoadDone && !model.error && (
                    <div className="flex min-h-0 flex-1 flex-col gap-[18px]">
                        <header className="flex shrink-0 flex-wrap items-center justify-between gap-3.5 md:items-end">
                            <div className="flex flex-col gap-1">
                                <h1 className="m-0 text-xl font-extrabold tracking-[-0.03em] text-k-ink md:text-[28px]">Pólizas</h1>
                                <p className="m-0 hidden text-sm text-k-muted md:block">Cartera de pólizas · {formatCount(model.totalItems)} registros</p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2.5">
                                <button
                                    type="button"
                                    aria-label="Actualizar"
                                    title="Actualizar"
                                    className="inline-flex h-11 w-11 items-center justify-center gap-2 rounded-xl border border-k-line bg-k-surface text-sm font-semibold text-k-ink hover:bg-k-hover md:w-auto md:px-4"
                                    onClick={model.reload}
                                >
                                    <RefreshIcon className={loading ? 'animate-spin' : ''} style={{ fontSize: 18 }} />
                                    <span className="sr-only md:not-sr-only">Actualizar</span>
                                </button>
                                <GrantContent permisos={[Permisos.POLIZAS_W]}>
                                    <Link
                                        to={NEW_POLICY_LOCATION.pathname}
                                        state={NEW_POLICY_LOCATION.state}
                                        aria-label="Nueva póliza"
                                        title="Nueva póliza"
                                        className="inline-flex h-11 w-11 items-center justify-center gap-2 rounded-xl bg-k-primary text-sm font-bold !text-white no-underline hover:brightness-105 hover:!text-white md:w-auto md:px-[18px]"
                                    >
                                        <AddIcon style={{ fontSize: 16 }} />
                                        <span className="sr-only md:not-sr-only">Nueva póliza</span>
                                    </Link>
                                </GrantContent>
                            </div>
                        </header>

                        <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[18px] border border-k-line bg-k-surface">
                            <div className="flex shrink-0 items-start gap-2.5 border-b border-k-line px-4 py-3.5">
                                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5">
                                    <LookupField label="Cliente" value={filters.cliente} search={searchClientes} onChange={model.changeCliente} />
                                    <LookupField label="Vendedor" value={filters.vendedor} search={searchVendedores} onChange={model.changeVendedor} />
                                    {TEXT_FILTERS.map((item) => (
                                        <TextField
                                            key={item.key}
                                            label={item.label}
                                            variant="outlined"
                                            size="small"
                                            value={filters[item.key]}
                                            sx={{ ...filterSx, width: { xs: '100%', sm: item.width } }}
                                            onChange={(event) => model.changeText(item.key, event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') model.submitText()
                                            }}
                                        />
                                    ))}
                                    <FilterSelect
                                        label="Vigencia"
                                        minWidth={120}
                                        value={filters.anio === '' ? '' : String(filters.anio)}
                                        onChange={(event) => model.changeAnio(event.target.value)}
                                    >
                                        <MenuItem value="">Todas</MenuItem>
                                        {YEARS.map((year) => <MenuItem key={year} value={String(year)}>{year}</MenuItem>)}
                                    </FilterSelect>
                                    <FilterSelect
                                        label="Método de pago"
                                        minWidth={210}
                                        value={filters.conducto}
                                        onChange={(event) => model.changeConducto(event.target.value)}
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        {model.conductos.map((item) => (
                                            <MenuItem key={item.key} value={item.key}>{item.value}</MenuItem>
                                        ))}
                                    </FilterSelect>
                                    <FilterSelect
                                        label="Ramo"
                                        minWidth={140}
                                        value={filters.ramo}
                                        onChange={(event) => model.changeRamo(event.target.value)}
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        {RAMOS.map((ramo) => <MenuItem key={ramo} value={ramo}>{ramo}</MenuItem>)}
                                    </FilterSelect>
                                    <FormControlLabel
                                        sx={{ mx: 0, color: 'rgb(var(--k-ink))', '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                                        control={(
                                            <Checkbox
                                                size="small"
                                                checked={filters.esRobot}
                                                sx={{ color: 'rgb(var(--k-muted))', '&.Mui-checked': { color: 'rgb(var(--k-primary))' } }}
                                                onChange={(event) => model.changeEsRobot(event.target.checked)}
                                            />
                                        )}
                                        label="Pendientes de validar"
                                    />
                                </div>
                                <button
                                    type="button"
                                    title="Limpiar filtros"
                                    aria-label="Limpiar filtros"
                                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center self-start rounded-xl bg-k-danger-soft text-k-danger-ink hover:brightness-95"
                                    onClick={model.clearFilters}
                                >
                                    <CloseIcon style={{ fontSize: 18 }} />
                                </button>
                            </div>

                            <div className={`min-h-0 flex-1 overflow-auto ${loading ? 'opacity-60' : ''}`}>
                                <table className="w-full table-fixed border-collapse text-sm md:min-w-[980px] md:table-auto">
                                    {tableHead}
                                    <tbody className="bg-k-surface">
                                        {model.rows.length === 0 && (
                                            <tr>
                                                <td className="px-4 py-12 text-center text-sm text-k-muted md:hidden">
                                                    No se encontraron pólizas con los filtros aplicados.
                                                </td>
                                                <td colSpan={columns.length} className="hidden px-4 py-12 text-center text-sm text-k-muted md:table-cell">
                                                    No se encontraron pólizas con los filtros aplicados.
                                                </td>
                                            </tr>
                                        )}
                                        {model.rows.map((row) => {
                                            const selected = model.detailId === row.id
                                            return (
                                                <tr
                                                    key={row.id}
                                                    className={`cursor-pointer border-t border-k-line transition-colors ${selected ? 'bg-k-accent-soft' : 'hover:bg-k-hover'}`}
                                                    onClick={() => model.openPoliza(row.id)}
                                                >
                                                    <td className="max-w-[280px] px-4 py-2.5">
                                                        <div className="flex min-w-0 items-center gap-3 text-left">
                                                            <RamoMark ramo={row.ramo} boxed />
                                                            <span className="flex min-w-0 flex-col">
                                                                <span className="flex items-center gap-1">
                                                                    <span className="truncate font-bold tabular-nums text-k-ink">{row.noPoliza || '—'}</span>
                                                                    {row.esRobot && (
                                                                        <span title="Creada automáticamente con la sincronización" className="shrink-0 text-tone-orange-ink">
                                                                            <WarningRoundedIcon style={{ fontSize: 16 }} />
                                                                        </span>
                                                                    )}
                                                                </span>
                                                                <span className="truncate text-xs text-k-muted md:hidden">{row.name || 'Sin cliente'}</span>
                                                                <span className="hidden truncate text-xs text-k-muted md:block">{row.ramo || 'Sin ramo'}</span>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="hidden max-w-[240px] px-2.5 py-2.5 md:table-cell">
                                                        <span className="flex min-w-0 flex-col">
                                                            <span className="truncate font-medium text-k-ink">{row.name || '—'}</span>
                                                            <span className={`truncate text-xs ${row.seller ? 'text-k-muted' : 'italic text-k-muted'}`}>
                                                                {row.seller || 'Sin vendedor'}
                                                            </span>
                                                        </span>
                                                    </td>
                                                    <td className="hidden max-w-[220px] px-2.5 py-2.5 lg:table-cell">
                                                        <span className={`block truncate ${row.concepto ? 'text-k-ink-2' : 'italic text-k-muted'}`}>
                                                            {row.concepto || 'Sin concepto'}
                                                        </span>
                                                    </td>
                                                    <td className="hidden px-2.5 py-2.5 lg:table-cell">
                                                        <span className="flex min-w-0 flex-col">
                                                            <span className="truncate text-k-ink-2">{row.aseguradora || '—'}</span>
                                                            <span className="truncate text-xs tabular-nums text-k-muted">{row.agenteClave || ''}</span>
                                                        </span>
                                                    </td>
                                                    <td className="hidden whitespace-nowrap px-2.5 py-2.5 md:table-cell">
                                                        <span className={`inline-flex items-center gap-1.5 rounded-full px-[9px] py-[3px] text-xs font-bold ${row.status.className}`}>
                                                            <span className="h-1.5 w-1.5 rounded-full bg-current" />
                                                            {row.status.label}
                                                        </span>
                                                    </td>
                                                    <td className="hidden px-2.5 py-2.5 lg:table-cell">
                                                        <span className="flex min-w-[150px] flex-col gap-1">
                                                            <span className="whitespace-nowrap text-[13px] text-k-ink-2">{row.vigencia.label}</span>
                                                            <span className="h-1 overflow-hidden rounded-sm bg-k-line" role="img" aria-label={`Avance de vigencia ${row.vigencia.progress}%`}>
                                                                <span className={`block h-full rounded-sm ${row.vigencia.barClass}`} style={{ width: `${row.vigencia.progress}%` }} />
                                                            </span>
                                                            {row.vigencia.note && <span className={`text-[11px] font-bold ${row.vigencia.noteClass}`}>{row.vigencia.note}</span>}
                                                        </span>
                                                    </td>
                                                    <td className="hidden whitespace-nowrap px-2.5 py-2.5 text-right font-bold tabular-nums text-k-ink md:table-cell">
                                                        {money(row.total, row.moneda)}
                                                    </td>
                                                    <td className="hidden whitespace-nowrap px-4 py-2.5 text-right md:table-cell">
                                                        <div className="inline-flex justify-end gap-2">
                                                            <Button
                                                                variant="outlined"
                                                                aria-label={`Ver póliza ${row.noPoliza || ''}`}
                                                                sx={editBtnSx}
                                                                onMouseDown={stopRowClick}
                                                                onClick={(event) => {
                                                                    event.stopPropagation()
                                                                    model.viewPoliza(row.id)
                                                                }}
                                                            >
                                                                Ver
                                                            </Button>
                                                            <GrantContent permisos={[Permisos.POLIZAS_W]}>
                                                                <Button
                                                                    variant="outlined"
                                                                    aria-label={`Editar póliza ${row.noPoliza || ''}`}
                                                                    sx={editBtnSx}
                                                                    onMouseDown={stopRowClick}
                                                                    onClick={(event) => {
                                                                        event.stopPropagation()
                                                                        model.editPoliza(row.id)
                                                                    }}
                                                                >
                                                                    Editar
                                                                </Button>
                                                            </GrantContent>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-k-line bg-k-surface px-4 py-3 text-sm text-k-muted">
                                <span className="hidden md:inline">Mostrando {formatCount(model.rangeFrom)} a {formatCount(model.rangeTo)} de {formatCount(model.totalItems)} pólizas</span>
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        aria-label="Página anterior"
                                        disabled={model.page <= 1 || loading}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover disabled:opacity-40"
                                        onClick={() => model.setPage(model.page - 1)}
                                    >
                                        <ChevronLeftIcon fontSize="small" />
                                    </button>
                                    {model.pages.map((number) => (
                                        <button
                                            key={number}
                                            type="button"
                                            aria-current={number === model.page ? 'page' : undefined}
                                            disabled={loading}
                                            className={`inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2 text-sm font-medium disabled:opacity-40 ${number === model.page
                                                ? 'border-k-primary bg-k-accent-soft text-k-accent-ink'
                                                : 'border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover'}`}
                                            onClick={() => model.setPage(number)}
                                        >
                                            {number}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        aria-label="Página siguiente"
                                        disabled={model.page >= model.totalPages || loading}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover disabled:opacity-40"
                                        onClick={() => model.setPage(model.page + 1)}
                                    >
                                        <ChevronRightIcon fontSize="small" />
                                    </button>
                                    <Select
                                        size="small"
                                        value={model.pageSize}
                                        disabled={loading}
                                        onChange={(event) => model.changePageSize(Number(event.target.value))}
                                        inputProps={{ 'aria-label': 'Registros por página' }}
                                        className="ml-1 h-8 min-w-[72px] rounded-md bg-k-surface text-sm text-k-ink [&_.MuiOutlinedInput-notchedOutline]:border-k-line [&_.MuiSelect-select]:min-h-0 [&_.MuiSelect-select]:py-0 [&_.MuiSelect-select]:leading-8 [&_.MuiSelect-select]:text-k-ink [&_.MuiSvgIcon-root]:text-k-muted"
                                        MenuProps={{
                                            slotProps: {
                                                paper: {
                                                    className: 'rounded-md border border-k-line bg-k-surface text-k-ink [&_.MuiMenuItem-root]:text-k-ink [&_.MuiMenuItem-root.Mui-selected]:bg-k-accent-soft [&_.MuiMenuItem-root.Mui-selected]:text-k-accent-ink',
                                                },
                                            },
                                        }}
                                    >
                                        {PAGE_SIZES.map((size) => (
                                            <MenuItem key={size} value={size}>{size}</MenuItem>
                                        ))}
                                    </Select>
                                </div>
                            </div>
                        </section>
                    </div>
                )}
            </div>
        </KarteraAdminTemplate>
    )
}

export default PolicyList
